/**
 * OnboardingModal - Modal de boas-vindas com carousel
 * Mostra diferentes conteúdos baseado no tipo de usuário (PJ vs PF)
 */

import { Modal, Button } from '@finansafe/design-system';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { authService } from '../services/AuthService';

interface OnboardingModalProps {
    isOpen: boolean;
    onClose: () => void;
    isEnterprise: boolean;
}

interface OnboardingSlide {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    features?: string[];
}

// Conteúdo para usuários PJ (Enterprise)
const enterpriseSlides: OnboardingSlide[] = [
    {
        id: 'finance-management',
        title: 'Gestão Financeira Empresarial',
        description: 'Acompanhe receitas, despesas, fluxo de caixa e índices de liquidez da sua empresa de forma profissional.',
        imageUrl: '/gestaofinanceira-pj.png',
        features: [
            'Controle completo de receitas e despesas',
            'Análise de fluxo de caixa',
            'Índices de liquidez em tempo real',
            'Relatórios financeiros detalhados'
        ]
    },
    {
        id: 'strategic-plan',
        title: 'Plano Estratégico',
        description: 'Desenvolva estratégias personalizadas para crescimento e otimização do seu negócio com IA.',
        imageUrl: '/planoestrategico-pj.png',
        features: [
            'Estratégias personalizadas com IA',
            'Até 80 requisições de planos estratégicos',
            'Análise de mercado e concorrência',
            'Roadmap de crescimento empresarial'
        ]
    },
    {
        id: 'business-plan',
        title: 'Plano de Negócio Completo',
        description: 'Crie planos de negócio profissionais baseados nas estratégias desenvolvidas.',
        imageUrl: '/planodenegocio-pj.png',
        features: [
            'Planos de negócio estruturados',
            'Até 80 sugestões de IA para planos de ação',
            'Templates profissionais',
            'Análise de viabilidade financeira'
        ]
    }
];

// Conteúdo para usuários PF (Pessoa Física)
const personalSlides: OnboardingSlide[] = [
    {
        id: 'finance-management',
        title: 'Gestão Financeira Pessoal',
        description: 'Acompanhe suas receitas, despesas e o crescimento dos seus negócios pessoais.',
        imageUrl: '/gestaofinanceira-pf.png',
        features: [
            'Controle de receitas e despesas pessoais',
            'Acompanhamento de metas financeiras',
            'Análise de gastos por categoria',
            'Relatórios de evolução patrimonial'
        ]
    },
    {
        id: 'extra-income',
        title: 'Renda Extra',
        description: 'Descubra novas oportunidades de ganhar dinheiro com suas habilidades e interesses.',
        imageUrl: '/rendaextra-pf.png',
        features: [
            'Sugestões personalizadas de renda extra',
            'Até 40 requisições de oportunidades',
            'Análise de compatibilidade com seu perfil',
            'Estratégias de monetização'
        ]
    },
    {
        id: 'business-plan',
        title: 'Plano de Negócio',
        description: 'Transforme suas ideias de renda extra em planos de negócio estruturados.',
        imageUrl: '/planodenegocio-pf.png',
        features: [
            'Planos baseados em suas oportunidades',
            'Até 40 sugestões de IA para planos de ação',
            'Estruturação profissional de ideias',
            'Validação de modelos de negócio'
        ]
    }
];

export default function OnboardingModal({ isOpen, onClose, isEnterprise }: OnboardingModalProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);

    const slides = isEnterprise ? enterpriseSlides : personalSlides;
    const isLastSlide = currentSlide === slides.length - 1;


    const handleNext = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePrevious = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleComplete = async () => {
        setIsCompleting(true);
        try {
            const response = await authService.completeOnboarding();
            if (response.success) {
                onClose();
            } else {
                console.error('Erro ao completar onboarding:', response.message);
                // Mesmo com erro, fechamos o modal para não bloquear o usuário
                onClose();
            }
        } catch (error) {
            console.error('Erro ao completar onboarding:', error);
            // Mesmo com erro, fechamos o modal para não bloquear o usuário
            onClose();
        } finally {
            setIsCompleting(false);
        }
    };


    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xl"
            closeOnBackdropClick={false}
            showCloseButton={false}
        >
            <div className="relative max-h-[85vh] overflow-y-auto">

                {/* Indicadores de progresso */}
                <div className="flex justify-center mb-6">
                    <div className="flex space-x-2">
                        {slides.map((_, index) => (
                            <div
                                key={index}
                                className={`w-3 h-3 rounded-full transition-colors ${index === currentSlide
                                    ? 'bg-blue-600'
                                    : index < currentSlide
                                        ? 'bg-blue-300'
                                        : 'bg-gray-200'
                                    }`}
                            />
                        ))}
                    </div>
                </div>

                {/* Conteúdo do slide atual */}
                <div className="text-center mb-8">
                    {/* Imagem do slide - scrollável se necessário */}
                    <div className="w-full max-h-[28rem] bg-gray-50 rounded-lg mb-6 overflow-auto border border-gray-200 shadow-inner">
                        <div className="p-2">
                            <img
                                src={slides[currentSlide].imageUrl}
                                alt={slides[currentSlide].title}
                                className="w-full h-auto rounded-md shadow-sm"
                                style={{ minHeight: '200px' }}
                                onError={(e) => {
                                    // Fallback caso a imagem não carregue
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        parent.innerHTML = `<div class="flex items-center justify-center h-64 bg-gray-100 rounded-md"><span class="text-gray-500 text-sm font-medium">Erro ao carregar: ${slides[currentSlide].title}</span></div>`;
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* Título e descrição */}
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                        {slides[currentSlide].title}
                    </h2>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                        {slides[currentSlide].description}
                    </p>

                    {/* Features */}
                    {slides[currentSlide].features && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-6">
                            <ul className="space-y-2 text-left">
                                {slides[currentSlide].features.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0" />
                                        <span className="text-gray-700 text-sm">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Navegação */}
                <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
                    {/* Botão Anterior */}
                    {currentSlide > 0 ? (
                        <Button
                            variant="ghost"
                            onClick={handlePrevious}
                            className="flex items-center gap-2"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Anterior
                        </Button>
                    ) : (
                        <div></div>
                    )}

                    {/* Botão Próximo ou Começar */}
                    <div className="flex gap-2">
                        {isLastSlide ? (
                            <Button
                                onClick={handleComplete}
                                disabled={isCompleting}
                                variant="primary"
                                className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 px-6 py-2"
                            >
                                {isCompleting ? 'Finalizando...' : 'Começar'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNext}
                                variant="primary"
                                className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600 px-6 py-2"
                            >
                                Próximo →
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
